import { Image } from "react-bootstrap";
import { useQuery } from "react-query";

import {
	getDefaultAvatar,
	getOfferingAvatars,
	getOrgAvatar,
	getUserAvatars,
} from "../api";
import DefaultProductIcon from "../assets/img/default-product-icon.png";
import { OrgDto, ProductDto, UserDto } from "../types/dto";
import { lifetimeDataQuerySetting } from "./customQueryClientProvider";
import { UserNameWithEmail } from "./userNameWithEmail";

interface AvatarAndTextProps {
	text?: string;
	avatar_Base64: any;
	size?: number;
	rounded?: boolean;
	children?: any;
}

export const AvatarAndText = ({
	text,
	avatar_Base64,
	size = 16,
	rounded = false,
	children,
}: AvatarAndTextProps) => (
	<div
		style={{ display: "flex", alignItems: "center" }}
		data-testid="test-avatartext"
	>
		<Image
			className={`me-2 d-inline ${rounded ? "rounded-circle" : ""}`}
			src={avatar_Base64 || DefaultProductIcon}
			width={size}
			height={size}
		/>
		{text}
		{children}
	</div>
);

export const OrgAvatarAndText = ({
	org,
	size = 16,
}: {
	org: OrgDto;
	size?: number;
}) => {
	const { data: orgProfilePic } = useQuery(
		[Object.keys({ getOrgAvatar })[0], org.id],
		() => getOrgAvatar({ orgId: org.id }),
		{
			...lifetimeDataQuerySetting,
			enabled: org.id !== "",
		},
	);

	return (
		<AvatarAndText
			avatar_Base64={orgProfilePic?.avatar_Base64}
			rounded={false}
			size={size}
		>
			{org.name}
		</AvatarAndText>
	);
};

export const OfferingAvatarAndText = ({
	offering,
	text = "",
	size = 16,
}: {
	offering: ProductDto;
	size?: number;
	text?: string;
}) => {
	const offeringId = offering?.id;
	const { data } = useQuery(
		[Object.keys({ getOfferingAvatars })[0], offeringId],
		() =>
			getOfferingAvatars({
				offeringIds: [offeringId],
			}),
		{
			...lifetimeDataQuerySetting,
			enabled: !!offeringId,
		},
	);

	return (
		<AvatarAndText
			avatar_Base64={data?.offeringAvatars[0]?.avatar_Base64}
			rounded={false}
			size={size}
		>
			{text || offering.name}
		</AvatarAndText>
	);
};

export const UserAvatarAndText = ({
	user,
	size = 16,
	isShortened = false,
}: {
	user: UserDto;
	size?: number;
	isShortened?: boolean;
}) => {
	if (user == null) return;

	const { data: userProfilePic } = useQuery(
		[Object.keys({ getUserAvatars })[0], user.id],
		() => getUserAvatars([user.id]),
		{
			...lifetimeDataQuerySetting,
		},
	);
	const { data: defaultAvatar } = useQuery(
		[Object.keys({ getDefaultAvatar })[0]],
		() => getDefaultAvatar(),
		{
			...lifetimeDataQuerySetting,
		},
	);

	const text = isShortened ? (
		user?.firstName?.trim() +
		" " +
		user?.lastName?.trim()?.slice(0, 1) +
		"."
	) : (
		<UserNameWithEmail {...user} />
	);

	return (
		<AvatarAndText
			avatar_Base64={
				userProfilePic?.avatars[0]?.avatar_Base64 || defaultAvatar
			}
			rounded={true}
			size={size}
		>
			{text}
		</AvatarAndText>
	);
};
